// Distance calculation utilities for proximity notifications

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export interface SuperchargerWithDistance extends Coordinates {
  id: string;
  name: string;
  distance: number; // in kilometers
  [key: string]: any;
}

/**
 * Calculate the distance between two coordinates using the Haversine formula
 * @param coord1 First coordinate
 * @param coord2 Second coordinate
 * @returns Distance in kilometers
 */
export const calculateDistance = (coord1: Coordinates, coord2: Coordinates): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRadians(coord2.latitude - coord1.latitude);
  const dLon = toRadians(coord2.longitude - coord1.longitude);
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(coord1.latitude)) * Math.cos(toRadians(coord2.latitude)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  
  return Math.round(distance * 10) / 10; // Round to 1 decimal place
};

/**
 * Convert degrees to radians
 */
const toRadians = (degrees: number): number => {
  return degrees * (Math.PI / 180);
};

/**
 * Convert radians to degrees
 */
const toDegrees = (radians: number): number => {
  return radians * (180 / Math.PI);
};

/**
 * Calculate the bearing (direction) from one point to another
 * @param from Starting coordinate
 * @param to Ending coordinate  
 * @returns Bearing in degrees (0-360, where 0 is north)
 */
export const calculateBearing = (from: Coordinates, to: Coordinates): number => {
  const dLon = toRadians(to.longitude - from.longitude);
  const lat1 = toRadians(from.latitude);
  const lat2 = toRadians(to.latitude);
  
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  
  const bearing = toDegrees(Math.atan2(y, x));
  
  // Normalize to 0-360 degrees
  return (bearing + 360) % 360;
};

/**
 * Find the nearest superchargers to a given location
 * @param userLocation User's current location
 * @param superchargers Array of superchargers
 * @param count Number of nearest superchargers to return (default: 3)
 * @returns Array of nearest superchargers with distance
 */
export const findNearestSuperchargers = (
  userLocation: Coordinates,
  superchargers: any[],
  count: number = 3
): SuperchargerWithDistance[] => {
  if (!userLocation || !superchargers.length) {
    return [];
  }

  // Calculate distances and add to supercharger objects
  const superchargersWithDistance: SuperchargerWithDistance[] = superchargers.map(charger => ({
    ...charger,
    distance: calculateDistance(userLocation, {
      latitude: charger.latitude,
      longitude: charger.longitude
    })
  }));

  // Sort by distance and return top N
  return superchargersWithDistance
    .sort((a, b) => a.distance - b.distance)
    .slice(0, count);
};

/**
 * Check if user is within proximity threshold of any supercharger
 * @param userLocation User's current location
 * @param superchargers Array of superchargers
 * @param thresholdKm Distance threshold in kilometers (default: 10)
 * @returns Nearest supercharger within threshold, or null
 */
export const checkProximityThreshold = (
  userLocation: Coordinates,
  superchargers: any[],
  thresholdKm: number = 10
): SuperchargerWithDistance | null => {
  const nearest = findNearestSuperchargers(userLocation, superchargers, 1);

  if (nearest.length === 0) {
    return null;
  }

  const nearestCharger = nearest[0];
  return nearestCharger.distance <= thresholdKm ? nearestCharger : null;
};

/**
 * Convert kilometers to miles
 * @param km Distance in kilometers
 * @returns Distance in miles
 */
export const kmToMiles = (km: number): number => {
  return km * 0.621371;
};

/**
 * Format distance based on unit system preference
 * @param distanceKm Distance in kilometers
 * @param unitSystem User's unit system preference ('metric' | 'imperial')
 * @returns Formatted distance string
 */
export const formatDistance = (distanceKm: number, unitSystem: 'metric' | 'imperial' = 'metric'): string => {
  if (unitSystem === 'imperial') {
    const miles = kmToMiles(distanceKm);
    if (miles < 0.1) {
      return '< 0.1 mi';
    }
    return `${miles.toFixed(1)} mi`;
  } else {
    // For metric, use meters for better precision on short distances
    const meters = Math.round(distanceKm * 1000);
    if (meters < 10) {
      return '< 10 m';
    }
    return `${meters} m`;
  }
};